// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
/**
 * Dado de leitura da fila de pedidos de revisão da SME.
 *
 * A API não existe: não há rota de `solicitacoes_revisao_medicao` em
 * `apps/api/src/modulos/` nem tabela no banco. O mock entra sob a exceção
 * registrada em `CLAUDE.md` para esta tela — só leitura, dado claramente
 * fictício, e some antes de qualquer promoção `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist de `docs/ALVOS.md`).
 *
 * AS DATAS SÃO RELATIVAS AO RELÓGIO DA EXECUÇÃO, de propósito. Competência
 * gravada à mão envelhece: a tela filtra por competência e ordena por data, e
 * um mock datado de um mês morto esvazia a tela sozinho meses depois — e
 * derruba teste numa PR que não encostou nesta tela.
 */

import { useQuery } from '@tanstack/react-query';

import { MOCK_DRES } from '../dados-simulados';
import type { DreDaRevisao, FiltrosDeRevisao, RevisaoMedicaoSme } from './tipos';

/** Competência `yyyy-mm` deslocada em meses a partir do mês corrente. */
function competencia(mesesAtras: number): string {
  const hoje = new Date();
  const data = new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

/** Instante ISO de `dias` atrás — o "solicitado em" de cada pedido. */
function diasAtras(dias: number): string {
  return new Date(Date.now() - dias * 86_400_000).toISOString();
}

/** Uma DRE da lista simulada, já no formato que a tela consome. */
function dre(indice: number): DreDaRevisao {
  const encontrada = MOCK_DRES[indice];
  if (!encontrada?.codigo || !encontrada.nome) {
    throw new Error(`DRE simulada ${indice} não existe ou está incompleta em MOCK_DRES.`);
  }
  return { id: encontrada.id, codigo: encontrada.codigo, nome: encontrada.nome };
}

/**
 * Oito pedidos, cobrindo as cinco situações e as três ordenações da tela:
 * pendentes recentes e antigos, um valor muito acima dos outros, um já
 * encaminhado ao DTP e dois já respondidos (um aceito, um rejeitado).
 */
function revisoesSimuladas(): RevisaoMedicaoSme[] {
  const anterior = competencia(1);
  const retrasado = competencia(2);

  return [
    {
      id: 'rev-001',
      medicaoId: 'med-001',
      motivo:
        'Três veículos da linha 412 rodaram nos dias 12, 13 e 14 e não constam na apuração. Os diários de bordo foram anexados ao processo.',
      status: 'PENDENTE',
      parecerSme: null,
      respondidoEm: null,
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(11),
      medicao: {
        id: 'med-001',
        mesReferencia: anterior,
        valorTotal: '184320.55',
        credenciado: {
          id: 'cred-001',
          nome: 'Transportes Aurora Boreal Ltda.',
          cpfCnpj: '11.222.333/0001-44',
          dreId: dre(0).id,
          dre: dre(0),
        },
      },
    },
    {
      id: 'rev-002',
      medicaoId: 'med-002',
      motivo:
        'A tarifa aplicada é a da tabela anterior. O reajuste publicado passou a valer no primeiro dia da competência.',
      status: 'PENDENTE',
      parecerSme: null,
      respondidoEm: null,
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(4),
      medicao: {
        id: 'med-002',
        mesReferencia: anterior,
        valorTotal: '962104.00',
        credenciado: {
          id: 'cred-002',
          nome: 'Cooperativa Vale do Horizonte',
          cpfCnpj: '55.666.777/0001-88',
          dreId: dre(1).id,
          dre: dre(1),
        },
      },
    },
    {
      id: 'rev-003',
      medicaoId: 'med-003',
      motivo:
        'Foram descontados 6 dias por falta de monitor, mas a substituta estava cadastrada e consta na escala entregue à DRE.',
      status: 'PENDENTE',
      parecerSme: null,
      respondidoEm: null,
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(2),
      medicao: {
        id: 'med-003',
        mesReferencia: anterior,
        valorTotal: '73890.10',
        credenciado: {
          id: 'cred-003',
          nome: 'Vanessa Portela Ferreira — MEI',
          cpfCnpj: '123.456.789-09',
          dreId: dre(4).id,
          dre: dre(4),
        },
      },
    },
    {
      id: 'rev-004',
      medicaoId: 'med-004',
      motivo:
        'O veículo placa XYZ-1A23 entrou em manutenção no dia 8 e o reserva assumiu a rota. A apuração considerou a rota parada.',
      status: 'EM_ANALISE',
      parecerSme: null,
      respondidoEm: null,
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(9),
      medicao: {
        id: 'med-004',
        mesReferencia: anterior,
        valorTotal: '256770.30',
        credenciado: {
          id: 'cred-004',
          nome: 'Expresso Serra Azul Transportes S.A.',
          cpfCnpj: '99.888.777/0001-66',
          dreId: dre(6).id,
          dre: dre(6),
        },
      },
    },
    {
      id: 'rev-005',
      medicaoId: 'med-005',
      motivo:
        'A quantidade de crianças atendidas na rota 08 está menor que a lista da escola. Pedimos conferência do cadastro.',
      status: 'ENCAMINHADA_DTP',
      parecerSme:
        'Divergência confirmada na conferência com a unidade escolar. Não é caso de erro de apuração, e sim de cadastro de beneficiário — encaminhado ao DTP para recálculo da competência.',
      respondidoEm: diasAtras(6),
      encaminhadaDtpEm: diasAtras(6),
      criadaEm: diasAtras(19),
      medicao: {
        id: 'med-005',
        mesReferencia: retrasado,
        valorTotal: '411250.90',
        credenciado: {
          id: 'cred-005',
          nome: 'Rota Sul Transporte Escolar Ltda.',
          cpfCnpj: '33.444.555/0001-22',
          dreId: dre(10).id,
          dre: dre(10),
        },
      },
    },
    {
      id: 'rev-006',
      medicaoId: 'med-006',
      motivo: 'Dois dias letivos de reposição não foram contados na apuração.',
      status: 'ACEITA',
      parecerSme:
        'Os dois dias constam no calendário de reposição homologado pela DRE. Pedido aceito e devolvido para ajuste na competência.',
      respondidoEm: diasAtras(13),
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(24),
      medicao: {
        id: 'med-006',
        mesReferencia: retrasado,
        valorTotal: '128640.75',
        credenciado: {
          id: 'cred-006',
          nome: 'Caminho da Alvorada Transportes ME',
          cpfCnpj: '77.111.222/0001-55',
          dreId: dre(2).id,
          dre: dre(2),
        },
      },
    },
    {
      id: 'rev-007',
      medicaoId: 'med-007',
      motivo: 'Solicito revisão do desconto aplicado por atraso na entrega da apólice.',
      status: 'REJEITADA',
      parecerSme:
        'A apólice foi entregue 11 dias após o prazo previsto no termo. O desconto seguiu a regra vigente e fica mantido.',
      respondidoEm: diasAtras(16),
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(27),
      medicao: {
        id: 'med-007',
        mesReferencia: retrasado,
        valorTotal: '96110.20',
        credenciado: {
          id: 'cred-007',
          nome: 'Osvaldo Brandão Martins — Transporte Escolar',
          cpfCnpj: '987.654.321-00',
          dreId: dre(11).id,
          dre: dre(11),
        },
      },
    },
    {
      id: 'rev-008',
      medicaoId: 'med-008',
      motivo:
        'O valor do espelho não bate com o demonstrativo enviado pela DRE. Diferença de aproximadamente R$ 4.200,00.',
      status: 'PENDENTE',
      parecerSme: null,
      respondidoEm: null,
      encaminhadaDtpEm: null,
      criadaEm: diasAtras(31),
      medicao: {
        id: 'med-008',
        mesReferencia: retrasado,
        valorTotal: '58402.40',
        credenciado: {
          id: 'cred-008',
          nome: 'Guaraná Transportes Escolares Eireli',
          cpfCnpj: '44.555.666/0001-99',
          dreId: dre(8).id,
          dre: dre(8),
        },
      },
    },
  ];
}

/** Sem acento e sem caixa — a busca do protótipo casa "guarana" com "Guaraná". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function filtrarRevisoes(
  revisoes: RevisaoMedicaoSme[],
  filtros: FiltrosDeRevisao,
): RevisaoMedicaoSme[] {
  let base = revisoes;

  if (filtros.status !== 'TODOS') {
    base = base.filter((r) => r.status === filtros.status);
  }

  if (filtros.dreIds.length > 0) {
    const escolhidas = new Set(filtros.dreIds);
    base = base.filter((r) => escolhidas.has(r.medicao.credenciado.dreId));
  }

  const busca = filtros.busca.trim();
  if (busca) {
    const alvo = normalizar(busca);
    base = base.filter(
      (r) =>
        normalizar(r.medicao.credenciado.nome).includes(alvo) ||
        normalizar(r.medicao.credenciado.cpfCnpj).includes(alvo),
    );
  }

  return base;
}

/**
 * Fila de pedidos de revisão da SME.
 *
 * Mantém `useQuery` — e não um `useMemo` sobre o arranjo — porque a tela precisa
 * dos quatro estados de verdade (CLAUDE.md, regras de interface, item 3) e
 * porque a troca para o endpoint real, quando ele existir, fica sendo só o
 * corpo de `queryFn`.
 */
export function useRevisoesSme(filtros: FiltrosDeRevisao) {
  return useQuery({
    queryKey: ['financeiro', 'revisoes-sme', filtros],
    queryFn: async () => filtrarRevisoes(revisoesSimuladas(), filtros),
    staleTime: 30_000,
  });
}
